// ═══════════════════════════════════════════════════════════════════════
// Crash's hold R, per round (hard close rev 4.1, existing-data-f).
//
// Every crash clip bakes ONE 3 s arming beat: beacon 0 → 3, and the climb
// starts at beacon 3 (CRASH_REVEAL_DELAY_SECONDS). Today the engine fires
// race_started at beacon 0, so the hold R is 3 and the event clock
// (now − race_started) reads the beacon's clock. A hard round with a shorter
// hold fires race_started at beacon 3 − R instead: the first 3 − R s of the
// beat play inside the lock window, and the climb comes R after race_started.
// R is read from the round's own race_ready (`hold_s`: crash always sends its
// clip's R, 3 today).
//
// So every EVENT-clock reader anchors on race_started − (3 − R): the event
// clock then reads the beacon's clock on every round, and every 3 s on the
// beacon side stays. At R 3, and on any round that is not hard, the anchor is
// race_started's own stamp: byte-identical to today.
//
// And the 3-2-1, which today starts with race_started, starts from the lock
// signal on such a round: the beat begins X − (3 − R) after the lock.
// ═══════════════════════════════════════════════════════════════════════

import type { RaceEvent, RaceReadyEvent } from 'src/types/domain/RaceEvent'
import { CRASH_REVEAL_DELAY_SECONDS } from './crashCurve'

const isReady = (event: RaceEvent): event is RaceReadyEvent => event.eventType === 'race_ready'

/**
 * R for the round, seconds: a hard round's `hold_s` when it is under 3 s,
 * else 3 (today's hold, and never a negative shift).
 */
export function crashRoundHoldS(raceEvents: readonly RaceEvent[]): number {
  const hold = raceEvents.find(isReady)?.payload?.hardClose?.holdS
  return typeof hold === 'number' && hold > 0 && hold < CRASH_REVEAL_DELAY_SECONDS
    ? hold
    : CRASH_REVEAL_DELAY_SECONDS
}

/**
 * The round's event-clock origin, ms epoch: race_started's stamp moved back
 * by 3 − R, so `(now − origin) / 1000` reads the beacon's clock and the climb
 * is at 3 on it. Undefined before race_started.
 */
export function crashEventClockOriginMs(raceEvents: readonly RaceEvent[]): number | undefined {
  const started = raceEvents.find((event) => event.eventType === 'race_started')
  if (!started) return undefined
  const stampMs = new Date(started.eventTimestamp).getTime()
  const beatLeadS = CRASH_REVEAL_DELAY_SECONDS - crashRoundHoldS(raceEvents)
  return beatLeadS > 0 ? stampMs - beatLeadS * 1000 : stampMs
}

/**
 * The beacon's clock as the lock reads it, before race_started arrives, on a
 * round with a short hold: race_started comes X after the lock and is 3 − R
 * into the beat, so the beat starts at lock + X − (3 − R) and the climb is at
 * lock + X + R. Undefined without a lock moment, and at R 3, where the 3-2-1
 * waits for race_started as today.
 */
export function lockBeatSeconds(input: {
  /** When the board locked, `performance.now()` ms. */
  lockedAt: number | undefined
  /** X, seconds. */
  lockLeadS: number
  /** R, seconds. */
  holdS: number
  /** `performance.now()` ms. */
  now: number
}): number | undefined {
  if (input.lockedAt === undefined || input.holdS >= CRASH_REVEAL_DELAY_SECONDS) return undefined
  const beatStartS = input.lockLeadS - (CRASH_REVEAL_DELAY_SECONDS - input.holdS)
  return (input.now - input.lockedAt) / 1000 - beatStartS
}

/**
 * The centre's narrative from the lock to race_started, on the beat's clock:
 * the stage-setter before the beat, then 3‥2‥1 over it. It holds on 1 past
 * the beat's end: nothing climbs before race_started.
 */
export function crashLockArming(
  beatS: number | undefined
): { phase: 'betting' } | { phase: 'arming'; count: number } | undefined {
  if (beatS === undefined) return undefined
  if (beatS < 0) return { phase: 'betting' }
  const count = Math.max(1, Math.min(3, Math.ceil(CRASH_REVEAL_DELAY_SECONDS - beatS)))
  return { phase: 'arming', count }
}

/**
 * The centre bar from the lock to the beat, on a short-hold round, 0..100 left:
 * full at the lock and empty as the 3 appears, on the same beat clock the
 * 3-2-1 reads, so the bar hands straight over to the count. One drain and no
 * digits (owner 10-29: the count of X there read 5 4 3, then 3 2 1 again).
 * Undefined with no beat reading, or when the beat starts at the lock itself.
 */
export function lockGapBarFill(input: {
  /** `lockBeatSeconds`, or the beacon's beat reading. */
  beatS: number | undefined
  /** X, seconds. */
  lockLeadS: number
  /** R, seconds. */
  holdS: number
}): number | undefined {
  if (input.beatS === undefined) return undefined
  const gapS = input.lockLeadS - (CRASH_REVEAL_DELAY_SECONDS - input.holdS)
  if (!(gapS > 0)) return undefined
  return Math.min(100, Math.max(0, (-input.beatS / gapS) * 100))
}
